<template>
    <a-popup :visible="modelValue" @cancel="$emit('update:modelValue', false)">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[800px] tw-h-fit tw-overflow-auto tw-flex tw-flex-col tw-border tw-border-solid dark:tw-border-gray-400 tw-border-gray-300 tw-shadow-md tw-my-16 tw-rounded">
            <div>
                <div class="tw-p-4 tw-text-lg tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-solid tw-font-semibold dark:tw-text-gray-700 tw-text-gray-500 dark:tw-bg-gray-100 tw-bg-gray-50">
                    <span>{{ $t('common.marketers') }}</span>
                </div>

                <div class="tw-p-5 tw-bg-white tw-flex-1">
                    <div v-if="loading" class="tw-flex tw-justify-center tw-items-center tw-py-6">
                        <icon icon="eos-icons:three-dots-loading" class="tw-text-3xl tw-text-gray-400" />
                        <p class="tw-ml-2 tw-text-gray-500">{{ $t('common.loading-data') }}</p>
                    </div>
                    <div v-else-if="error" class="tw-text-center tw-py-4 tw-text-rose-500">
                        {{ error }}
                    </div>
                    <div v-else>
                        <div class="tw-flex sm:tw-items-center tw-justify-between tw-mb-2 tw-gap-3 tw-px-2 sm:tw-flex-row tw-flex-col">
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <p class="tw-p-2x tw-font-bold tw-text-base">{{ $t('common.marketers') }}</p>
                            </div>

                            <div class="tw-flex tw-items-center tw-gap-2">
                                <!-- Sorting Buttons -->
                                <select @change="sortMarketers(sortDirection)" v-model="sortField"
                                    class="tw-p-0.5 tw-text-[11px] tw-min-w-[70px] tw-outline-none tw-px-2 tw-max-h-[35px] tw-h-full hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600">
                                    <option value="total_orders">{{ $t('common.orders') }}</option>
                                    <option value="confirmed_orders">{{ $t('common.confirmed') }}</option>
                                    <option value="confirmation_rate">{{ $t('common.confirmation_rate') }}</option>
                                    <option value="reported_orders">{{ $t('common.reported') }}</option>
                                    <option value="reporting_rate">{{ $t('common.reporting-rate') }}</option>
                                    <option value="no_answer_orders">{{ $t('common.no-answer') }}</option>
                                    <option value="no_answer_rate">{{ $t('common.no-answer-rate') }}</option>
                                    <option value="canceled_orders">{{ $t('common.canceled') }}</option>
                                    <option value="cancelation_rate">{{ $t('common.cancelation-rate') }}</option>
                                    <option value="duplicated_orders">{{ $t('common.duplicated') }}</option>
                                    <option value="duplication_rate">{{ $t('common.duplication-rate') }}</option>
                                    <option value="delivered_orders">{{ $t('common.delivered') }}</option>
                                    <option value="delivery_rate">{{ $t('common.delivery_rate') }}</option>
                                </select>

                                <button @click="sortMarketers('asc')"
                                    class="tw-p-0.5 tw-px-2 hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600">
                                    <icon icon="iconamoon:arrow-up-2-bold" class="tw-text-gray-600" />
                                </button>
                                <button @click="sortMarketers('desc')"
                                    class="tw-p-0.5 tw-px-2 hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600">
                                    <icon icon="iconamoon:arrow-down-2-bold" class="tw-text-gray-600" />
                                </button>

                                <!-- Toggle Percentage Button -->
                                <button @click="isPercentage = !isPercentage"
                                    class="tw-p-0.5 tw-px-2 hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600">
                                    <icon icon="icon-park-outline:switch" />
                                </button>
                            </div>
                        </div>

                        <div v-if="!marketers.length" class="tw-min-h-[237px] tw-flex tw-items-center tw-justify-center tw-p-2 tw-bg-gray-50 tw-text-gray-600 tw-text-center tw-text-sm tw-rounded">
                            {{ $t('messages.table-no-results') }}
                        </div>

                        <div v-else class="tw-mt-4x tw-space-y-2 tw-min-h-[237px]">
                            <div v-for="marketer in sortedMarketers" :key="marketer.name"
                                class="tw-w-full tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-p-2 tw-flex md:tw-items-center tw-justify-between md:tw-flex-row tw-flex-col">
                                <div class="tw-flex tw-items-center tw-gap-2">
                                    <div class="tw-relative tw-rounded-full tw-w-8 tw-h-8 tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-border tw-border-solid tw-border-gray-200">
                                        <icon icon="lets-icons:user-fill" class="tw-text-gray-300 tw-text-2xl" />
                                        <div v-if="online.some(i => i.id == marketer.id)"
                                            class="tw-absolute tw-bottom-0 tw-right-0 tw-w-2 tw-h-2 tw-aspect-square tw-bg-emerald-400 tw-border tw-border-solid tw-border-white tw-rounded-full">
                                        </div>
                                    </div>
                                    <div class="tw-h-full tw-flex tw-flex-col tw-justify-between tw-me-5">
                                        <span class="tw-font-semibold tw-whitespace-nowrap md:tw-text-base tw-text-sm">{{ marketer.name }}</span>
                                    </div>
                                </div>
                                <div class="tw-gap-4 tw-flex tw-items-center tw-flex-wrap md:tw-mt-0 tw-mt-4">
                                    <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                                        <span class="tw-text-xs tw-text-gray-600">{{ $t('common.orders') }}</span>
                                        <span class="tw-text-xs tw-font-bold tw-text-black">{{ marketer.total_orders }}</span>
                                    </div>

                                    <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                                        <span class="tw-text-xs tw-text-gray-600">{{ $t('common.confirmed') }}</span>
                                        <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-sky-500">{{ marketer.confirmation_rate }}%</span>
                                        <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-sky-500">{{ marketer.confirmed_orders }}</span>
                                    </div>

                                    <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                                        <span class="tw-text-xs tw-text-gray-600">{{ $t('common.reported') }}</span>
                                        <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-violet-500">{{ marketer.reporting_rate }}%</span>
                                        <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-violet-500">{{ marketer.reported_orders }}</span>
                                    </div>

                                    <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                                        <span class="tw-text-xs tw-text-gray-600">{{ $t('common.no-answer') }}</span>
                                        <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-amber-500">{{ marketer.no_answer_rate }}%</span>
                                        <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-amber-500">{{ marketer.no_answer_orders }}</span>
                                    </div>

                                    <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                                        <span class="tw-text-xs tw-text-gray-600">{{ $t('common.canceled') }}</span>
                                        <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-rose-500">{{ marketer.cancelation_rate }}%</span>
                                        <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-rose-500">{{ marketer.canceled_orders }}</span>
                                    </div>

                                    <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                                        <span class="tw-text-xs tw-text-gray-600">{{ $t('common.duplicated') }}</span>
                                        <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-stone-500">{{ marketer.duplication_rate }}%</span>
                                        <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-stone-500">{{ marketer.duplicated_orders }}</span>
                                    </div>

                                    <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                                        <span class="tw-text-xs tw-text-gray-600">{{ $t('common.delivered') }}</span>
                                        <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-green-500">{{ parseFloat(marketer.delivery_rate + marketer.settled_rate).toFixed(2) }}%</span>
                                        <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-green-500">{{ marketer.delivered_orders + marketer.settled_orders }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { useUserStore } from '@/stores';
import { marketers as fetchMarketers } from '@/api/products';

const userStore = useUserStore();
const online = computed(() => userStore.online);

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    productId: {
        type: [String, Number],
        required: true
    }
});

const emit = defineEmits(['update:modelValue']);
emit
const loading = ref(false);
const error = ref(null);
const isPercentage = ref(true);
const sortField = ref('total_orders');
const sortDirection = ref('asc');
const marketers = ref([]);

const fetchData = async () => {
    loading.value = true;
    error.value = null;
    
    try {
        const response = await fetchMarketers(props.productId);
        if (response.data.code === 'SUCCESS') {
            marketers.value = response.data.marketers;
            sortMarketers('desc');
        } else {
            error.value = response.data.message || 'Failed to load marketers data';
        }
    } catch (err) {
        console.error('Error fetching marketers:', err);
        error.value = 'An error occurred while loading marketers data';
    } finally {
        loading.value = false;
    }
};

// Computed property to get sorted marketers
const sortedMarketers = computed(() => {
    return [...marketers.value].sort((a, b) => {
        if (sortDirection.value === 'asc') {
            return a[sortField.value] > b[sortField.value] ? 1 : -1;
        } else {
            return a[sortField.value] < b[sortField.value] ? 1 : -1;
        }
    });
});

// Method to sort marketers
const sortMarketers = (direction) => {
    sortDirection.value = direction;
};

// Watch for modelValue changes to fetch data when popup opens
watch(() => props.modelValue, (newValue) => {
    if (newValue) {
        fetchData();
    }
});
</script>

<style></style> 